import { CSSProperties, HTMLAttributes, ReactNode } from 'react'
import clsx from 'clsx'
import {
  PagebuilderMaxContentWidthOptions,
  PagebuilderSectionBackgroundImageFit,
  PagebuilderSectionBackgroundImagePosition,
  PagebuilderSectionBackgroundType
} from 'cms/constants/pagebuilder'

import { DeviceType } from 'shared/ui/utils/breakpoints'
import { waveEdgeClipPath } from 'shared/ui/utils/wavedEdges'

import {
  PagebuilderMaxContentWidth,
  PagebuilderSectionSettings
} from '../types'

import styles from './SectionWrapper.module.scss'

export const waveClipPathHeightDesktop = 100
export const waveClipPathHeightMobile = 30

interface SectionWrapperProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode
  settings?: PagebuilderSectionSettings
}

interface ResolvedPadding {
  paddingTop: number
  paddingRight: number
  paddingBottom: number
  paddingLeft: number
}

interface ResolvedMargin {
  marginTop: number
  marginBottom: number
}

const getPaddingByDevice = (
  settings: PagebuilderSectionSettings | undefined,
  device: DeviceType
): ResolvedPadding => {
  switch (device) {
    case DeviceType.desktop:
      return {
        paddingTop:
          settings?.backgroundType ===
          PagebuilderSectionBackgroundType.transparent
            ? 0
            : (settings?.spacingDesktopPaddingTop ?? 0),
        paddingRight: settings?.spacingDesktopPaddingRight ?? 0,
        paddingBottom:
          settings?.backgroundType ===
          PagebuilderSectionBackgroundType.transparent
            ? 0
            : (settings?.spacingDesktopPaddingBottom ?? 0),
        paddingLeft: settings?.spacingDesktopPaddingLeft ?? 0
      }
    case DeviceType.tablet:
      return {
        paddingTop:
          settings?.backgroundType ===
          PagebuilderSectionBackgroundType.transparent
            ? 0
            : (settings?.spacingTabletPaddingTop ?? 0),
        paddingRight: settings?.spacingTabletPaddingRight ?? 0,
        paddingBottom:
          settings?.backgroundType ===
          PagebuilderSectionBackgroundType.transparent
            ? 0
            : (settings?.spacingTabletPaddingBottom ?? 0),
        paddingLeft: settings?.spacingTabletPaddingLeft ?? 0
      }
    case DeviceType.mobile:
      return {
        paddingTop:
          settings?.backgroundType ===
          PagebuilderSectionBackgroundType.transparent
            ? 0
            : (settings?.spacingMobilePaddingTop ?? 0),
        paddingRight: settings?.spacingMobilePaddingRight ?? 0,
        paddingBottom:
          settings?.backgroundType ===
          PagebuilderSectionBackgroundType.transparent
            ? 0
            : (settings?.spacingMobilePaddingBottom ?? 0),
        paddingLeft: settings?.spacingMobilePaddingLeft ?? 0
      }
    default: {
      const _exhaustive: never = device

      return _exhaustive
    }
  }
}

const getMarginByDevice = (
  settings: PagebuilderSectionSettings | undefined,
  device: DeviceType
): ResolvedMargin => {
  switch (device) {
    case DeviceType.desktop:
      return {
        marginTop: settings?.spacingDesktopMarginTop
          ? (settings?.spacingDesktopMarginTopValue ?? 0)
          : 0,
        marginBottom: settings?.spacingDesktopMarginBottom
          ? (settings?.spacingDesktopMarginBottomValue ?? 0)
          : 0
      }
    case DeviceType.tablet:
      return {
        marginTop: settings?.spacingTabletMarginTop
          ? (settings?.spacingTabletMarginTopValue ?? 0)
          : 0,
        marginBottom: settings?.spacingTabletMarginBottom
          ? (settings?.spacingTabletMarginBottomValue ?? 0)
          : 0
      }
    case DeviceType.mobile:
      return {
        marginTop: settings?.spacingMobileMarginTop
          ? (settings?.spacingMobileMarginTopValue ?? 0)
          : 0,
        marginBottom: settings?.spacingMobileMarginBottom
          ? (settings?.spacingMobileMarginBottomValue ?? 0)
          : 0
      }
    default: {
      const _exhaustive: never = device

      return _exhaustive
    }
  }
}

const getResolvedBackgroundColor = (
  settings: PagebuilderSectionSettings | undefined
): string => {
  if (
    settings?.backgroundType === PagebuilderSectionBackgroundType.transparent
  ) {
    return 'transparent'
  }

  return settings?.backgroundColor ?? 'transparent'
}

const getResolvedBackgroundImageUrl = (
  settings: PagebuilderSectionSettings | undefined
): string | undefined => {
  if (settings?.backgroundType !== PagebuilderSectionBackgroundType.image) {
    return undefined
  }

  return settings.backgroundImageUrl
}

const getResolvedBackgroundImageFit = (
  settings: PagebuilderSectionSettings | undefined
): PagebuilderSectionBackgroundImageFit => {
  if (settings?.backgroundType !== PagebuilderSectionBackgroundType.image) {
    return PagebuilderSectionBackgroundImageFit.cover
  }

  return (
    settings.backgroundImageFit ?? PagebuilderSectionBackgroundImageFit.cover
  )
}

const getResolvedBackgroundImagePosition = (
  settings: PagebuilderSectionSettings | undefined
): PagebuilderSectionBackgroundImagePosition => {
  if (settings?.backgroundType !== PagebuilderSectionBackgroundType.image) {
    return PagebuilderSectionBackgroundImagePosition.center
  }

  return (
    settings.backgroundImagePosition ??
    PagebuilderSectionBackgroundImagePosition.center
  )
}

const getResolvedTextColor = (
  settings: PagebuilderSectionSettings | undefined
): string => {
  return settings?.textColor ?? 'inherit'
}

const formatPadding = (padding: ResolvedPadding) =>
  `${padding.paddingTop}px ${padding.paddingRight}px ${padding.paddingBottom}px ${padding.paddingLeft}px`

const getSectionMaxWidth = (
  maxContentWidth: PagebuilderMaxContentWidth | undefined
): string => {
  if (maxContentWidth === PagebuilderMaxContentWidthOptions.full) {
    return '100%'
  }

  if (typeof maxContentWidth === 'number') {
    return `${maxContentWidth}px`
  }

  return 'none'
}

const SectionWrapper = ({
  children,
  settings,
  className,
  ...props
}: SectionWrapperProps) => {
  const backgroundColor = getResolvedBackgroundColor(settings)
  const backgroundImageUrl = getResolvedBackgroundImageUrl(settings)
  const backgroundImageFit = getResolvedBackgroundImageFit(settings)
  const backgroundImagePosition = getResolvedBackgroundImagePosition(settings)
  const textColor = getResolvedTextColor(settings)
  const showWaves =
    settings?.backgroundType === PagebuilderSectionBackgroundType.filled ||
    settings?.backgroundType === PagebuilderSectionBackgroundType.image
  const showWaveTop = Boolean(showWaves && settings?.wavedEdgesTop)
  const showWaveBottom = Boolean(showWaves && settings?.wavedEdgesBottom)
  const desktopMargin = getMarginByDevice(settings, DeviceType.desktop)
  const tabletMargin = getMarginByDevice(settings, DeviceType.tablet)
  const mobileMargin = getMarginByDevice(settings, DeviceType.mobile)
  const desktopPadding = getPaddingByDevice(settings, DeviceType.desktop)
  const tabletPadding = getPaddingByDevice(settings, DeviceType.tablet)
  const mobilePadding = getPaddingByDevice(settings, DeviceType.mobile)

  const sectionStyle = {
    '--section-bg-color': backgroundColor,
    '--section-bg-image': backgroundImageUrl
      ? `url(${backgroundImageUrl})`
      : 'none',
    '--section-bg-size': backgroundImageFit,
    '--section-bg-position': backgroundImagePosition,
    '--section-text-color': textColor,
    '--section-margin-top': `${desktopMargin.marginTop + (showWaveTop ? waveClipPathHeightDesktop : 0)}px`,
    '--section-margin-bottom': `${desktopMargin.marginBottom + (showWaveBottom ? waveClipPathHeightDesktop : 0)}px`,
    '--section-margin-top-tablet': `${tabletMargin.marginTop + (showWaveTop ? waveClipPathHeightDesktop : 0)}px`,
    '--section-margin-bottom-tablet': `${tabletMargin.marginBottom + (showWaveBottom ? waveClipPathHeightDesktop : 0)}px`,
    '--section-margin-top-mobile': `${mobileMargin.marginTop + (showWaveTop ? waveClipPathHeightMobile : 0)}px`,
    '--section-margin-bottom-mobile': `${mobileMargin.marginBottom + (showWaveBottom ? waveClipPathHeightMobile : 0)}px`,
    '--section-wave-bg-color': backgroundColor,
    '--section-wave-clip-path': `polygon(${waveEdgeClipPath})`
  } as CSSProperties

  const contentStyle = {
    '--section-padding-desktop': formatPadding(desktopPadding),
    '--section-padding-tablet': formatPadding(tabletPadding),
    '--section-padding-mobile': formatPadding(mobilePadding),
    '--section-max-width': getSectionMaxWidth(settings?.maxContentWidth)
  } as CSSProperties

  return (
    <section
      className={clsx(
        styles.sectionContainer,
        {
          [styles.hideDesktop]: settings?.visibilityDesktop === false,
          [styles.hideTablet]: settings?.visibilityTablet === false,
          [styles.hideMobile]: settings?.visibilityMobile === false,
          [styles.waveTop]: showWaveTop,
          [styles.waveBottom]: showWaveBottom
        },
        className
      )}
      style={sectionStyle}
      {...props}
    >
      <div className={styles.sectionContent} style={contentStyle}>
        {children}
      </div>
    </section>
  )
}

export default SectionWrapper
